/**
 * Copyright © 2025-2026 Indisea Software LLC. All rights reserved.
 *
 * Proprietary and confidential. Licensed, not sold. Use is subject to the
 * license agreement between Indisea Software LLC and the licensee.
 * See LICENSE for full terms.
 *
 * SPDX-License-Identifier: LicenseRef-Indisea-Proprietary
 */

import React, { useState, useEffect } from "react";
import {
    TextField,
    Box,
    Paper,
    Stack,
    Typography,
    Button,
    FormControlLabel,
    Switch,
    MenuItem
} from "@mui/material";

import { useParams, useLocation } from "react-router-dom";
import apiHeaders from "./apiHeaders"
import { handleSessionExpiredClose } from "./logout";
import { setTenantId } from "./storageUtils";
import { POSTING_OPTIONS, DEFAULT_POSTING_OPTION } from "./postingOptions";

function ZampSettings() {
    // Destructured rather than kept as the params object: useParams() returns a new object on
    // every render, which cannot be used as an effect dependency.
    const { tenantId } = useParams();
    const location = useLocation();

    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [successMessage, setSuccessMessage] = useState("");

    // Retrieving realm id from url and saving to session storage
     useEffect(() => {
         if (tenantId && tenantId !== ":tenantId") {
             setTenantId(tenantId);
             window.dispatchEvent(new Event("TenantIdChange"));
         }
     }, [location.search, tenantId]);


    const handlePasswordChange = (event) => {
        setPassword(event.target.value);
        if (error) setError("");
    };

    // token authentication submit handle
    const handleTokenSubmit = async (event) => {
        event.preventDefault();
        const token = password;
        const data = {
            token,
            tenantId: sessionStorage.getItem("tenantId"),
        };

        if (password.length !== 64) {
            setSuccessMessage("")
            setError("API token must be exactly 64 characters.");
            return;
        }

        try {
            const response = await fetch(
                `${process.env.REACT_APP_CONNECTOR_API_URL}/api/auth/zamp`,
                {
                    method: "POST",
                    headers: {
                        ...apiHeaders,
                        "Authorization": `${sessionStorage.getItem('token')}`
                    },
                    body: JSON.stringify(data),
                }
            );

            if (response.status === 401) {
                handleSessionExpiredClose();
                return;
            }

            const result = await response.json();
            if (response.ok) {
                setSuccessMessage(result.message);
                setError("");
            } else {
                setError(`${result.message}`);
                setSuccessMessage("");

            }
        } catch (err) {
            setError("Internal server error. Please try again.");
            setSuccessMessage("");
        }
    };

    // **Handle dialog close & redirect**


    const [loadingOptions, setLoadingOptions] = useState(false);

    // Toggle option states
    const [syncEnabled, setSyncEnabled] = useState(false);
    const [calculationsEnabled, setCalculationsEnabled] = useState(false);
    const [loggingEnabled, setLoggingEnabled] = useState(false);
    const [recalculateEnabled, setRecalculateEnabled] = useState(false);
    const [defaultPostingOption, setDefaultPostingOption] = useState(DEFAULT_POSTING_OPTION);


    // Populating stored user options
    useEffect(() => {
        const fetchZampToken = async () => {
            setLoadingOptions(true);
            if (sessionStorage.getItem('tenantId')) {
                try {
                    const response = await fetch(
                        `${process.env.REACT_APP_CONNECTOR_API_URL}/api/zamp/token?tenantId=${sessionStorage.getItem('tenantId')}`,
                        {
                            method: "GET",
                            headers: {
                                ...apiHeaders,
                                "Authorization": `${sessionStorage.getItem('token')}`
                            }
                        }
                    );
                    if (response.status === 401) {
                        handleSessionExpiredClose();
                        return;
                    }
                    const data = await response.json();
                    console.log(data);
                    if (data?.token) {
                        setPassword(data.token); // Set the token in the textbox
                    }
                } catch (error) {
                    setError(error.message);// "Failed to fetch token. Please try again.");
                } finally {
                   // setLoadingOptions(false);
                }
            }
        };
        const fetchOptions = async () => {
            setLoadingOptions(true);
            if (sessionStorage.getItem('tenantId')) {
                try {
                    const response = await fetch(
                        `${process.env.REACT_APP_CONNECTOR_API_URL}/api/settings?tenantId=${sessionStorage.getItem('tenantId')}`,
                        {
                            method: "GET",
                            headers: {
                                ...apiHeaders,
                                "Authorization": `${sessionStorage.getItem('token')}`
                            }
                        }
                    );
                    if (response.status === 401) {
                        handleSessionExpiredClose();
                        return;
                    }
                    const data = await response.json();
                    setSyncEnabled(data["isSyncEnabled"]);
                    setCalculationsEnabled(data["isCalculationsEnabled"]);
                    setLoggingEnabled(data["isLoggingEnabled"]);
                    setRecalculateEnabled(data["isRecalculateEnabled"]);
                    setDefaultPostingOption(data["defaultPostingOption"] || DEFAULT_POSTING_OPTION);
                } catch (error) {
                    //setOptionsError("Failed to fetch options. Please try again.");
                } finally {
                    setLoadingOptions(false);
                }
            }
        };
        const fetchtoken = async () => {
            const urlParams = new URLSearchParams(window.location.search);
            const sessionId = urlParams.get("s_id");
            if (sessionId) {
                try {
                    const response = await fetch(
                        `${process.env.REACT_APP_CONNECTOR_API_URL}/api/session?sessionId=${sessionId}`,
                        {
                            method: "GET",
                            headers: {
                                ...apiHeaders
                            }
                        }
                    );
                    if (response.ok) {
                        const result = await response.json();
                        sessionStorage.setItem("token", result.token);

                    }
                    else {
                        const result = response.json();
                        setError(result.message);

                    }
                } catch (err) {
                    setError("Internal server error. Please try again.");
                }
                // Remove sessionId from URL without reloading the page
                urlParams.delete("s_id");
                const newUrl = `${window.location.pathname}${urlParams.toString() ? "?" + urlParams.toString() : ""}`;
                window.history.replaceState({}, "", newUrl);
            }
            fetchOptions();
            fetchZampToken();
        };
        fetchtoken();       
    }, []);

    const [saveSettingserror, setSaveSettingsError] = useState("");
    const [saveSettingsSuccessMessage, setSaveSettingsSuccessMessage] = useState("");

    // Handle custom user settings submit

    const handleSubmit = async (event) => {
        event.preventDefault()

        const data = {
            tenantId: tenantId,
            isSyncEnabled: syncEnabled,
            isCalculationsEnabled: calculationsEnabled,
            isLoggingEnabled: loggingEnabled,
            isRecalculateEnabled: recalculateEnabled,
            defaultPostingOption: defaultPostingOption,
        };
        try {
            const response = await fetch(
                `${process.env.REACT_APP_CONNECTOR_API_URL}/api/settings`,
                {
                    method: "POST",
                    headers: {
                        ...apiHeaders,
                        "Authorization": `${sessionStorage.getItem('token')}`
                    },
                    body: JSON.stringify(data),
                }
            );

            if (response.status === 401) {
                handleSessionExpiredClose();
                return;
            }

            if (response.ok) {
                setSaveSettingsSuccessMessage("Settings saved successfully!")
            } else {
                setSaveSettingsError(`Error: ${"Failed to save settings."}`);
                setSaveSettingsSuccessMessage("");
            }
        } catch (err) {
            setSaveSettingsError("Network Error: Unable to save settings. Please try again later.");
            setSaveSettingsSuccessMessage("");
        }
    };

    return (
        <Box display="flex" flexDirection="column" gap={2} p={2}>
            <Box sx={{ alignItems: "center" }}>
                <Typography
                    variant="h6"
                    sx={{ mb: 2, fontWeight: "bold", textAlign: "center" }}
                >
                    Zamp Tax Settings
                </Typography>
            </Box>

            <Box component="form" onSubmit={handleTokenSubmit}>
                <Paper elevation={6} sx={{ boxShadow: 4, borderRadius: 2, p: 4, maxWidth: 800, margin: "auto" }}>
                    <Stack spacing={2} direction="column" sx={{ width: "100%" }}>
                        <TextField
                            label="Zamp API Token"
                            type="password"
                            variant="outlined"
                            fullWidth
                            required
                            value={password}
                            onChange={handlePasswordChange}
                            name="password"
                        />
                        {error && <Typography color="error">{error}</Typography>}
                        {successMessage && (
                            <Typography color="success">{successMessage}</Typography>
                        )}
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            className="primary"
                            sx={{ width: "250px" }}
                        >
                            Update API Token
                        </Button>
                    </Stack>
                </Paper>
            </Box>

            <Box component="form" onSubmit={handleSubmit}>
                <Paper elevation={6} sx={{ boxShadow: 4, borderRadius: 2, p: 4, maxWidth: 800, margin: "auto" }}>
                    {loadingOptions ? (
                        <Typography>Loading options...</Typography>
                    ) : (
                        <></>
                    )}

                    <Stack spacing={3} direction="column" sx={{ width: "100%", mt: 2 }}>
                        <Box>
                            <FormControlLabel
                                control={
                                    <Switch checked={calculationsEnabled} onChange={() => setCalculationsEnabled(!calculationsEnabled)} />
                                }
                                label="Allow Zamp to handle tax calculations"
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", ml: 7 }}>
                                Enable Zamp to automatically calculate tax on transactions.
                            </Typography>
                        </Box>
                        <Box>
                            <FormControlLabel
                                control={
                                    <Switch checked={syncEnabled} onChange={() => {
                                        const newVal = !syncEnabled;
                                        setSyncEnabled(newVal);
                                        if (!newVal) setRecalculateEnabled(false);
                                    }} />
                                }
                                label="Sync transactions with Zamp"
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", ml: 7 }}>
                                Required for all transactions including historical transaction sync.
                            </Typography>
                        </Box>
                        <Box>
                            <FormControlLabel
                                control={
                                    <Switch checked={loggingEnabled} onChange={() => setLoggingEnabled(!loggingEnabled)} />
                                }
                                label="Retain Zamp logs for 30 days"
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", ml: 7 }}>
                                Enable debug logging for troubleshooting. Logs are retained for 30 days.
                            </Typography>
                        </Box>
                        <Box>
                            <FormControlLabel
                                control={
                                    <Switch checked={recalculateEnabled} disabled={!syncEnabled} onChange={() => setRecalculateEnabled(!recalculateEnabled)} />
                                }
                                label="Enable transaction sync recalculate"
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", ml: 7 }}>
                                Recalculate transactions for Zamp. Requires "Sync transactions" to be enabled.
                            </Typography>
                        </Box>
                        <Box>
                            <TextField
                                select
                                label="Default posting option for new customers"
                                size="small"
                                value={defaultPostingOption}
                                onChange={(e) => setDefaultPostingOption(e.target.value)}
                                sx={{ minWidth: 280 }}
                            >
                                {POSTING_OPTIONS.map(option => (
                                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                                ))}
                            </TextField>
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                                Applied to customers newly discovered from Xero. Existing customers keep whatever is set for them in Customer Entity Mapping.
                            </Typography>
                        </Box>
                        {saveSettingserror && <Typography color="error">{saveSettingserror}</Typography>}
                        {saveSettingsSuccessMessage && (
                            <Typography color="success">{saveSettingsSuccessMessage}</Typography>
                        )}
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            className="primary"
                            sx={{ width: "250px" }}
                        >
                            Save Settings
                        </Button>
                    </Stack>
                </Paper>
            </Box>

        </Box>
    );
}

export default ZampSettings;
